import { useAtomValue } from "jotai";
import { DetailTableRow } from "../../components/DetailTable";
import { scrollAndHighlightRow } from "../../utils/scrollAndHighlight";
import { useColorizedValue } from "../useColorizedValue";
import { unifiedMetricsAtom } from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { getLast12Months } from "@/utils/metrics/metricsUtils";

export function useProjectedFinancialsM05Data(): DetailTableRow[] {
    const unifiedMetrics = useAtomValue(unifiedMetricsAtom);

    const projectedRevenue =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE
        ];
    const projectedEbitdaMargin =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P
        ];
    const projectedGrossMargin =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P
        ];
    const projectedCashBalance =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH
        ];
    const projectedEbitda =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA
        ];
    const projectedSalesMarketingEfficiency =
        unifiedMetrics[
            FinanceLabField
                .ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY
        ];

    const revenueColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedRevenue?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        projectedRevenue?.source,
        projectedRevenue?.info.derived.label,
    );

    const ebitdaMarginColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedEbitdaMargin?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        projectedEbitdaMargin?.source,
        projectedEbitdaMargin?.info.derived.label,
    );

    const grossMarginColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedGrossMargin?.values),
            FinanceLabFieldType.PERCENTAGE,
        ) || "-",
        projectedGrossMargin?.source,
        projectedGrossMargin?.info.derived.label,
    );

    const cashBalanceColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedCashBalance?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        projectedCashBalance?.source,
        projectedCashBalance?.info.derived.label,
    );

    const ebitdaColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedEbitda?.values),
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        projectedEbitda?.source,
        projectedEbitda?.info.derived.label,
    );

    const salesMarketingEfficiencyColorized = useColorizedValue(
        formatFinanceLabValue(
            getLast12Months(projectedSalesMarketingEfficiency?.values),
            FinanceLabFieldType.MULTIPLE,
        ) || "-",
        projectedSalesMarketingEfficiency?.source,
        projectedSalesMarketingEfficiency?.info.derived.label,
    );

    return [
        {
            label: "Revenue",
            value: revenueColorized.value,
            valueClassName: revenueColorized.className,
            source: revenueColorized.source,
            sourceClassName: revenueColorized.sourceClassName,
            delta: projectedRevenue?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-projected-financials",
                    "Revenue",
                ),
        },
        {
            label: "EBITDA Margin %",
            value: ebitdaMarginColorized.value,
            valueClassName: ebitdaMarginColorized.className,
            source: ebitdaMarginColorized.source,
            sourceClassName: ebitdaMarginColorized.sourceClassName,
            delta: projectedEbitdaMargin?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-projected-financials",
                    "EBITDA Margin %",
                ),
        },
        {
            label: "Gross Margin %",
            value: grossMarginColorized.value,
            valueClassName: grossMarginColorized.className,
            source: grossMarginColorized.source,
            sourceClassName: grossMarginColorized.sourceClassName,
            delta: projectedGrossMargin?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-projected-financials",
                    "Gross Margin %",
                ),
        },
        {
            label: "Cash Balance",
            value: cashBalanceColorized.value,
            valueClassName: cashBalanceColorized.className,
            source: cashBalanceColorized.source,
            sourceClassName: cashBalanceColorized.sourceClassName,
            delta: projectedCashBalance?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-projected-financials",
                    "Cash Balance",
                ),
        },
        {
            label: "EBITDA",
            value: ebitdaColorized.value,
            valueClassName: ebitdaColorized.className,
            source: ebitdaColorized.source,
            sourceClassName: ebitdaColorized.sourceClassName,
            delta: projectedEbitda?.delta,
            onClick: () =>
                scrollAndHighlightRow("summary-projected-financials", "EBITDA"),
        },
        {
            label: "Sales & Marketing Efficiency",
            value: salesMarketingEfficiencyColorized.value,
            valueClassName: salesMarketingEfficiencyColorized.className,
            source: salesMarketingEfficiencyColorized.source,
            sourceClassName: salesMarketingEfficiencyColorized.sourceClassName,
            delta: projectedSalesMarketingEfficiency?.delta,
            onClick: () =>
                scrollAndHighlightRow(
                    "summary-projected-financials",
                    "Sales & Marketing Efficiency",
                ),
        },
    ];
}
